// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license, please see LICENSE.md in the
// project root for license information or contact permission@sei.cmu.edu for full terms.

import { Injectable, OnDestroy } from '@angular/core';
import { ComnAuthService, ComnSettingsService } from '@cmusei/crucible-common';
import * as signalR from '@microsoft/signalr';
import { Action, Evaluation, ItemStatus, Move, Duty, ScoringModel, Submission, Team, TeamMembership, User } from 'src/app/generated/cite.api';
import { ActionDataService } from 'src/app/data/action/action-data.service';
import { EvaluationDataService } from 'src/app/data/evaluation/evaluation-data.service';
import { MoveDataService } from '../data/move/move-data.service';
import { DutyDataService } from 'src/app/data/duty/duty-data.service';
import { ScoringModelDataService } from 'src/app/data/scoring-model/scoring-model-data.service';
import { SubmissionDataService } from 'src/app/data/submission/submission-data.service';
import { TeamDataService } from 'src/app/data/team/team-data.service';
import { TeamMembershipDataService } from '../data/team/team-membership-data.service';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { SystemMessageService } from 'src/app/services/system-message/system-message.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export enum ApplicationArea {
  home = '',
  admin = 'Admin'
}
@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private hubConnection: signalR.HubConnection;
  private applicationArea: ApplicationArea;
  private connectionPromise: Promise<void>;
  private isJoined = false;
  private teamId = '';
  private token = '';
  private unreachableReported = false;
  private unsubscribe$ = new Subject();

  constructor(
    private authService: ComnAuthService,
    private systemMessageService: SystemMessageService,
    private settingsService: ComnSettingsService,
    private actionDataService: ActionDataService,
    private evaluationDataService: EvaluationDataService,
    private moveDataService: MoveDataService,
    private dutyDataService: DutyDataService,
    private scoringModelDataService: ScoringModelDataService,
    private submissionDataService: SubmissionDataService,
    private teamDataService: TeamDataService,
    private teamMembershipDataService: TeamMembershipDataService,
    private userDataService: UserDataService
  ) {
    this.authService.user$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.reconnect();
    });
  }

  public startConnection(applicationArea: ApplicationArea): Promise<void> {
    if (this.connectionPromise && this.applicationArea === applicationArea) {
      return this.connectionPromise;
    }

    this.applicationArea = applicationArea;
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.getHubUrlWithAuth())
      .withStatefulReconnect()
      .withAutomaticReconnect(new RetryPolicy(120, 0, 5))
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection.onreconnected(() => {
      this.onConnected();
    });

    this.addHandlers();
    this.connectionPromise = this.hubConnection.start();
    this.connectionPromise
      .then(() => this.onConnected())
      .catch((err) => this.onStartFailed(err));

    return this.connectionPromise;
  }

  private getHubUrlWithAuth(): string {
    const accessToken = this.authService.getAuthorizationToken();
    if (accessToken !== this.token) {
      this.token = accessToken;
      if (!this.token) {
        location.reload();
      }
    }
    const hubUrl = `${this.settingsService.settings.ApiUrl}/hubs/main?bearer=${accessToken}`;
    return hubUrl;
  }

  private reconnect() {
    if (this.hubConnection != null) {
      this.hubConnection.stop().then(() => {
        this.hubConnection.baseUrl = this.getHubUrlWithAuth();
        this.connectionPromise = this.hubConnection.start();
        this.connectionPromise.then(() => {
          if (this.hubConnection.state !== signalR.HubConnectionState.Connected) {
            setTimeout(() => this.reconnect(), 500);
          } else {
            this.onConnected();
          }
        }).catch((err) => this.onStartFailed(err));
      });
    }
  }

  private onConnected() {
    this.unreachableReported = false;
    this.join();
  }

  // report once per outage, not on every retry
  private onStartFailed(err: any) {
    if (this.unreachableReported) {
      return;
    }
    this.unreachableReported = true;
    console.warn('CITE hub unreachable: ' + (err?.message ?? err));
    this.systemMessageService.displayMessage(
      'Live Updates Unavailable',
      'CITE could not connect to the CITE API for live updates. Changes made by others will not appear until the connection is restored. Refresh the page to try again.'
    );
  }

  public join() {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnection.invoke('Join' + this.applicationArea);
      this.isJoined = true;
      if (this.teamId) {
        setTimeout(() => this.switchTeam(this.teamId, this.teamId), 100);
      }
    }
  }

  public leave() {
    if (this.isJoined) {
      this.hubConnection.invoke('Leave' + this.applicationArea);
    }
    this.isJoined = false;
  }

  public switchTeam(oldTeamId: string, newTeamId: string) {
    if (this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      setTimeout(() => this.switchTeam(oldTeamId, newTeamId), 500);
    } else if (this.isJoined) {
      if (this.applicationArea !== ApplicationArea.admin) {
        this.hubConnection.invoke('switchTeam', [oldTeamId, newTeamId]);
        this.teamId = newTeamId;
      }
    }
  }

  private addHandlers() {
    this.addActionHandlers();
    this.addEvaluationHandlers();
    this.addMoveHandlers();
    this.addDutyHandlers();
    this.addScoringModelHandlers();
    this.addSubmissionHandlers();
    this.addTeamHandlers();
    this.addTeamMembershipHandlers();
    this.addUserHandlers();
  }

  private addActionHandlers() {
    this.hubConnection.on(
      'ActionUpdated', (action: Action) => {
        this.actionDataService.updateStore(action);
      }
    );

    this.hubConnection.on('ActionCreated', (action: Action) => {
      this.actionDataService.updateStore(action);
    });

    this.hubConnection.on('ActionDeleted', (id: string) => {
      this.actionDataService.deleteFromStore(id);
    });
  }

  private addEvaluationHandlers() {
    this.hubConnection.on(
      'EvaluationUpdated', (evaluation: Evaluation) => {
        this.evaluationDataService.updateStore(evaluation);
      }
    );

    this.hubConnection.on('EvaluationCreated', (evaluation: Evaluation) => {
      this.evaluationDataService.updateStore(evaluation);
    });

    this.hubConnection.on('EvaluationDeleted', (id: string) => {
      this.evaluationDataService.deleteFromStore(id);
    });
  }

  private addMoveHandlers() {
    this.hubConnection.on(
      'MoveUpdated', (move: Move) => {
        this.moveDataService.updateStore(move);
      }
    );

    this.hubConnection.on('MoveCreated', (move: Move) => {
      this.moveDataService.updateStore(move);
    });

    this.hubConnection.on('MoveDeleted', (id: string) => {
      this.moveDataService.deleteFromStore(id);
    });
  }

  private addSubmissionHandlers() {
    this.hubConnection.on(
      'SubmissionUpdated', (submission: Submission) => {
        this.submissionDataService.updateStore(submission);
      }
    );

    this.hubConnection.on('SubmissionCreated', (submission: Submission) => {
      this.submissionDataService.updateStore(submission);
    });

    this.hubConnection.on('SubmissionDeleted', (id: string) => {
      this.submissionDataService.deleteFromStore(id);
    });
  }

  private addDutyHandlers() {
    this.hubConnection.on(
      'DutyUpdated', (duty: Duty) => {
        this.dutyDataService.updateStore(duty);
      }
    );

    this.hubConnection.on('DutyCreated', (duty: Duty) => {
      this.dutyDataService.updateStore(duty);
    });

    this.hubConnection.on('DutyDeleted', (id: string) => {
      this.dutyDataService.deleteFromStore(id);
    });
  }

  private addScoringModelHandlers() {
    this.hubConnection.on(
      'ScoringModelUpdated', (scoringModel: ScoringModel) => {
        this.scoringModelDataService.updateStore(scoringModel);
      }
    );

    this.hubConnection.on('ScoringModelCreated', (scoringModel: ScoringModel) => {
      this.scoringModelDataService.updateStore(scoringModel);
    });

    this.hubConnection.on('ScoringModelDeleted', (id: string) => {
      this.scoringModelDataService.deleteFromStore(id);
    });
  }

  private addTeamHandlers() {
    this.hubConnection.on('TeamUpdated', (team: Team) => {
      this.teamDataService.updateStore(team);
    }
    );

    this.hubConnection.on('TeamCreated', (team: Team) => {
      this.teamDataService.updateStore(team);
    });

    this.hubConnection.on('TeamDeleted', (id: string) => {
      this.teamDataService.deleteFromStore(id);
    });
  }

  private addTeamMembershipHandlers() {
    this.hubConnection.on('TeamMembershipUpdated', (teamMembership: TeamMembership) => {
      this.teamMembershipDataService.updateStore(teamMembership);
    });

    this.hubConnection.on('TeamMembershipCreated', (teamMembership: TeamMembership) => {
      this.teamMembershipDataService.updateStore(teamMembership);
    });

    this.hubConnection.on('TeamMembershipDeleted', (id: string) => {
      this.teamMembershipDataService.deleteFromStore(id);
    });
  }

  private addUserHandlers() {
    this.hubConnection.on('UserUpdated', (user: User) => {
      this.userDataService.updateStore(user);
    }
    );

    this.hubConnection.on('UserCreated', (user: User) => {
      this.userDataService.updateStore(user);
    });

    this.hubConnection.on('UserDeleted', (id: string) => {
      this.userDataService.deleteFromStore(id);
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

}

class RetryPolicy {
  constructor(
    private maxSeconds: number,
    private minJitterSeconds: number,
    private maxJitterSeconds: number
  ) {}

  nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext
  ): number | null {
    let nextRetrySeconds = Math.pow(2, retryContext.previousRetryCount + 1);

    if (retryContext.elapsedMilliseconds / 1000 > this.maxSeconds) {
      location.reload();
    }

    nextRetrySeconds +=
      Math.floor(
        Math.random() * (this.maxJitterSeconds - this.minJitterSeconds + 1)
      ) + this.minJitterSeconds; // Add Jitter

    return nextRetrySeconds * 1000;
  }
}
